import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import NewsletterSignup from '../components/Newsletter/NewsletterSignup';

function Home() {
  const navigate = useNavigate();

  return (
    <Box>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'background.paper',
          pt: 8,
          pb: 6,
          backgroundImage: 'linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.3)), url("/hero-background.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed',
          color: 'white',
          minHeight: '80vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.2) 0%, rgba(0,0,0,0.4) 100%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Container
          maxWidth="lg"
          sx={{
            position: 'relative',
            zIndex: 1
          }}
        >
          <Typography
            component="h1"
            variant="h2"
            sx={{
              fontWeight: 'bold',
              mb: 4,
              textShadow: '2px 2px 4px rgba(0,0,0,0.3)'
            }}
            data-testid={"home-initial-header"}
          >
            Handcrafted Leather Goods
          </Typography>
          <Typography
            variant="h5"
            sx={{ mb: 4 }}
          >
            Quality craftsmanship meets modern design
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/configurator')}
            sx={{ mr: 2 }}
          >
            Wallet Designer
          </Button>
          <Button
            variant="outlined"
            size="large"
            onClick={() => navigate('/products')}
            sx={{
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: 'background.paper',
              }
            }}
          >
            View Collection
          </Button>
        </Container>
      </Box>

      {/* Featured Products Section */}
      <Container sx={{ py: 8 }} maxWidth="md">
        <Typography
          variant="h3"
          sx={{
            mb: 6,
            textAlign: 'center',
            fontWeight: 'medium',
            color: 'text.primary'
          }}
        >
          Our Most Popular Designs
        </Typography>
        <Grid
          container
          spacing={4}
          direction={{ xs: 'column', md: 'row' }}
          alignItems="stretch"
        >
          {featuredProducts.map((product) => (
            <Grid item xs={12} md={4} key={product.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  maxWidth: { xs: '100%', sm: '500px', md: '100%' },
                  mx: 'auto',
                  '&:hover': {
                    boxShadow: (theme) => theme.shadows[15],
                    transform: 'translateY(-4px)',
                  },
                  transition: 'all 0.3s ease-in-out'
                }}
              >
                <Box sx={{ position: 'relative', pt: '73.28%' }}>
                  <CardMedia
                    component="img"
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderBottom: '1px solid',
                      borderColor: 'divider'
                    }}
                    image={product.image}
                    alt={product.name}
                  />
                </Box>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Typography
                    gutterBottom
                    variant="h5"
                    component="h2"
                    sx={{
                      fontWeight: 'bold',
                      mb: 2
                    }}
                  >
                    {product.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 3 }}
                  >
                    {product.description}
                  </Typography>
                  <Button
                    href={product.etsyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 'auto',
                      textTransform: 'none',
                      py: 1.5,
                      '&:hover': {
                        transform: 'scale(1.02)',
                      }
                    }}
                  >
                    View on Etsy
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Configurator Promo Section */}
      <Container maxWidth="md">
        <Typography variant="h3" gutterBottom>
          Design Your Perfect Wallet
        </Typography>
        <Grid container alignItems="center" spacing={4} justifyContent={{ xs: 'center', md: 'space-between' }}>
          <Grid xs={12} md={6}>
            <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
              Interactive Custom Wallet Builder
            </Typography>
            <Box component="ul" sx={{ mb: 4 }}>
              {[
                'Choose from our signature wallet designs',
                'Select premium Italian leather colors',
                'Customize individual pockets and sections',
                'Create unique two-tone combinations',
                'Preview your design in real-time'
              ].map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{
                    typography: 'body1',
                    mb: 1.5,
                    '&::marker': {
                      color: 'primary.main'
                    }
                  }}
                >
                  {point}
                </Box>
              ))}
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate('/configurator')}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem'
                }}
              >
                Start Designing
              </Button>
            </Box>
          </Grid>
          <Grid xs={12} md={6}>
            <Box
              component="img"
              src="/configurator-preview.png"
              alt="Wallet Configurator Preview"
              sx={{
                border: '20px solid #ccc',
                width: '350px',
                height: 'auto',
                display: 'block',
                borderRadius: 1
              }}
            />
          </Grid>
        </Grid>
      </Container>

      {/* Newsletter Section */}
      <Container sx={{ py: 8 }} maxWidth="lg">
        <NewsletterSignup />
      </Container>
    </Box>
  );
}

// Sample featured products data
const featuredProducts = [
  {
    id: 1,
    name: 'Italian Leather Bifold Wallet',
    description: 'Crafted from Badalassi Carlo Pueblo leather, featuring four interior pockets, a thumb slot for easy access, and a cash flap. Each wallet is hand-stitched with durable Ritza Tiger thread.',
    image: 'https://i.etsystatic.com/51134566/r/il/1b9f04/6365775077/il_fullxfull.6365775077_ncba.jpg',
    etsyLink: 'https://kbrosleather.etsy.com/listing/1712731261/italian-leather-bifold-wallet-badalassi'
  },
  {
    id: 2,
    name: 'Minimalist Front Pocket Wallet',
    description: 'A sleek, slim design with three pockets for up to 8 cards and cash. Made from premium veg-tanned leather that develops a rich patina over time.',
    image: 'https://i.etsystatic.com/51134566/r/il/dd93a6/6367807029/il_fullxfull.6367807029_irbh.jpg',
    etsyLink: 'https://kbrosleather.etsy.com/listing/1705377479/minimalist-leather-wallet-handmade-3'
  },
  {
    id: 3,
    name: 'Italian Leather Magnetic Bookmark',
    description: 'Handmade with Badalassi Carlo Pueblo leather, featuring strong neodymium magnets and soft suede lining. Perfect for keeping your place while protecting your books.',
    image: 'https://i.etsystatic.com/51134566/r/il/b6ed57/6477250691/il_fullxfull.6477250691_jgi3.jpg',
    etsyLink: 'https://kbrosleather.etsy.com/listing/1825818665/italian-leather-magnetic-bookmark'
  }
];

export default Home;
